import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { NavigationStart, Router } from '@angular/router';
import { filter } from 'rxjs';

/** The head tags a page can own. Every field is optional; an absent field falls through to the CMS default. */
export interface PageSeo {
  title?: string;
  description?: string;
  /** Absolute URL. Also used as og:url. */
  canonical?: string;
  /** og:url when the page has no canonical (the CMS default passes the request URL here). */
  url?: string;
  ogType?: string;
  /** Absolute image URL for og:image, og:image:secure and twitter:image. */
  image?: string;
  twitterCard?: string;
}

/**
 * Single writer of the SEO head tags that both the old CMS and a page itself may want to set: title,
 * description, canonical and the Open Graph / Twitter set.
 *
 * `PageLoadService.updatePageMetaTags` applies the CMS page's values for every route through
 * `applyDefault`. A page that owns its SEO (the tutor profile) calls `set`. On client-side navigation the
 * two arrive in either order, so the page-owned values are remembered as an override that the CMS default
 * cannot clobber; every write is `override ?? default` per field. The override, and the `<link
 * rel="canonical">` it created, are dropped on the next `NavigationStart`, so nothing leaks to the next
 * page. Same pattern as `RobotsService`, which keeps the `robots` tag.
 */
@Injectable({ providedIn: 'root' })
export class PageSeoService {
  private override: PageSeo | null = null;

  constructor(
    private titleService: Title,
    private meta: Meta,
    @Inject(DOCUMENT) private document: Document,
    router: Router,
  ) {
    router.events
      .pipe(filter(event => event instanceof NavigationStart))
      .subscribe(() => {
        this.override = null;
        this.setCanonical(undefined);
      });
  }

  /** A page that owns its SEO. Wins over the CMS default for the rest of this navigation. */
  set(seo: PageSeo): void {
    this.override = seo;
    this.write(seo, {});
  }

  /** The CMS values. Fields a page-owned decision already set for this navigation are left alone. */
  applyDefault(defaults: PageSeo): void {
    this.write(this.override ?? {}, defaults);
  }

  private write(primary: PageSeo, fallback: PageSeo): void {
    const pick = <K extends keyof PageSeo>(key: K): PageSeo[K] => primary[key] ?? fallback[key];

    const title = pick('title');
    if (title !== undefined) {
      this.titleService.setTitle(title);
    }
    const description = pick('description');
    const url = pick('canonical') ?? pick('url');
    const image = pick('image');

    this.upsert('name', 'description', description);
    this.upsert('property', 'og:title', title);
    this.upsert('property', 'og:description', description);
    this.upsert('property', 'og:url', url);
    this.upsert('property', 'og:type', pick('ogType'));
    this.upsert('property', 'og:image', image);
    this.upsert('property', 'og:image:secure', image);
    this.upsert('name', 'twitter:title', title);
    this.upsert('name', 'twitter:description', description);
    this.upsert('name', 'twitter:card', pick('twitterCard'));
    // Only a page-owned image sets twitter:image; the CMS never did, so a default removes a leftover one.
    if (primary.image) {
      this.upsert('name', 'twitter:image', primary.image);
    } else {
      this.meta.removeTag('name="twitter:image"');
    }
    this.setCanonical(pick('canonical'));
  }

  /** Writes the tag when there is a value; an undefined value leaves an existing tag untouched. */
  private upsert(attr: 'name' | 'property', key: string, content: string | undefined): void {
    if (content === undefined) {
      return;
    }
    this.meta.updateTag({ [attr]: key, content: content ?? '' });
  }

  private setCanonical(href: string | undefined): void {
    const head = this.document?.head;
    if (!head) {
      return;
    }
    let link = head.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!href) {
      // removeChild rather than remove(): works on the server DOM as well as in browsers.
      if (link?.parentNode) {
        link.parentNode.removeChild(link);
      }
      return;
    }
    if (!link) {
      link = this.document.createElement('link');
      link.setAttribute('rel', 'canonical');
      head.appendChild(link);
    }
    link.setAttribute('href', href);
  }
}
